<template>
  <a
    class="alink"
    tabindex="0"
    :href="$props.href"
    :target="$props.target"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
  >
    <div
      class="alink__content"
      :class="classes"
    >
      <slot name="leading"></slot>
      <span
        v-if="$props.label"
        v-text="$props.label"
      />
      <slot name="trailing"></slot>
    </div>
  </a>
</template>

<script setup>
import { computed } from 'vue'

// INIT
defineEmits(['click'])
const props = defineProps({
  href: {
    type: String,
    default: undefined,
  },

  label: {
    type: String,
    default: undefined,
  },

  occurence: {
    validator(value) {
      return ['standalone', 'inline'].includes(value)
    },
    default: 'standalone',
  },

  size: {
    validator(value) {
      return ['body-1', 'body-2', 'body-3'].includes(value)
    },
    default: 'body-2',
  },

  target: {
    type: String,
    default: '_self',
  },
})

// COMPUTED
const classes = computed(() => {
  return {
    [`alink__content--${props.occurence}`]: true,
    [`alink__content--${props.size}`]: true,
  }
})
</script>

<style>
:root,
.appearance-positive {
  --pet-lnk-c: var(--Interaction-States-enabled-default);
  --pet-lnk-c-hovered: var(--Interaction-States-hovered-default-a);
  --pet-lnk-c-active: var(--Interaction-States-pressed-default-a);
  --pet-lnk-c-disabled: var(--Interaction-States-disabled-default-label);

  --pet-lnk-c-outline: var(--on-surface);
}

.appearance-negative {
  --pet-lnk-c: var(--Interaction-States-enabled-inverse);
  --pet-lnk-c-hovered: var(--Interaction-States-hovered-inverse-a);
  --pet-lnk-c-active: var(--Interaction-States-pressed-inverse-a);
  --pet-lnk-c-disabled: var(--Interaction-States-disabled-inverse-label);

  --pet-lnk-c-outline: var(--on-inverse-surface-variant);
}

.appearance-extended {
  --pet-lnk-c: var(--Interaction-States-enabled-extended);
  --pet-lnk-c-hovered: var(--Interaction-States-hovered-extended-a);
  --pet-lnk-c-active: var(--Interaction-States-pressed-extended-a);
  --pet-lnk-c-disabled: var(--Interaction-States-disabled-extended-label);

  --pet-lnk-c-outline: var(--on-surface);
}
</style>

<style scoped>
.alink {
  display: inline-block;
  text-decoration-line: none;

  &:hover .alink__content {
    color: var(--pet-lnk-c-hovered);
    border-color: var(--pet-lnk-c-hovered);
  }

  &:active .alink__content {
    color: var(--pet-lnk-c-active);
    border-color: var(--pet-lnk-c-active);
  }

  &[disabled='true'] {
    pointer-events: none;
    text-decoration: line-through;

    .alink__content {
      color: var(--pet-lnk-c-disabled);
      border-color: var(--pet-lnk-c-disabled);
    }
  }

  /* Focus outline handled by global focus.css */
}

.alink__content {
  display: flex;
  align-items: center;
  gap: var(--fixed-spacing-fix-02);
  transition-property: color, border-color;
  transition-duration: 150ms;
  transition-timing-function: ease-in;
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  color: var(--pet-lnk-c);
  border-color: var(--pet-lnk-c);
  /* width: max-content; @note: prevents text breaking */

  &--inline {
    border-bottom: 1px solid;
  }

  &--body-1 {
    font-size: var(--font-size-bold-lg);
    letter-spacing: var(--letter-spacing-bold-lg);
    line-height: var(--line-height-bold-lg);
  }

  &--body-2 {
    font-size: var(--font-size-bold-md);
    letter-spacing: var(--letter-spacing-bold-md);
    line-height: var(--line-height-bold-md);
  }

  &--body-3 {
    font-size: var(--font-size-bold-sm);
    letter-spacing: var(--letter-spacing-bold-sm);
    line-height: var(--line-height-bold-sm);
  }
}

@media (--v-large) {
  .alink__content {
    &--body-1 {
      font-size: var(--font-size-bold-xl);
      letter-spacing: var(--letter-spacing-bold-xl);
    }
  }
}

@media (--v-wide) {
  .alink__content {
    &--body-1 {
      font-size: var(--font-size-bold-2xl);
      letter-spacing: var(--letter-spacing-bold-2xl);
    }
  }
}
</style>
